import {
    type PropsWithChildren,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useViewerId } from '@/entities/viewer';

import { useLazyGetMigrationStatusQuery } from '../../api/plan-migration.api';
import {
    getPlanMigrationData,
    getPlanMigrationIsOpen,
} from '../../model/selectors/plan-migration.selectors';
import { planMigrationActions } from '../../model/slices/plan-migration.slice';
import { AccountSelectionPopup } from '../account-selection-popup/account-selection-popup';
import { PlanMigrationPopup } from '../plan-migration-popup/plan-migration-popup';

const isPostMigrationPage = () => {
    const url = new URL(window.location.href);

    return (
        url.pathname === '/thank-you' &&
        url.searchParams.get('migration') === 'success'
    );
};

export function PlanMigrationProvider(props: PropsWithChildren) {
    const { children } = props;

    const dispatch = useDispatch();
    const viewerId = useViewerId();
    const isOpen = useSelector(getPlanMigrationIsOpen);
    const migrationData = useSelector(getPlanMigrationData);

    const [isAccountSelectionOpen, setIsAccountSelectionOpen] = useState(false);

    const [getMigrationStatus] = useLazyGetMigrationStatusQuery();

    useEffect(() => {
        if (!viewerId || isPostMigrationPage()) {
            return;
        }

        const fetchStatus = async () => {
            try {
                const result = await getMigrationStatus().unwrap();

                if (result.requires_migration) {
                    dispatch(planMigrationActions.showPopup(result));
                } else if (result.needs_account_selection) {
                    setIsAccountSelectionOpen(true);
                }
            } catch {
                // Migration status check failed — let the user through,
                // the reactive listener will catch 403s on subsequent API calls
            }
        };

        fetchStatus();
    }, [dispatch, getMigrationStatus, viewerId]);

    const handleAccountSelectionClose = useCallback(() => {
        setIsAccountSelectionOpen(false);
    }, []);

    return (
        <>
            {children}
            {isOpen && migrationData && (
                <PlanMigrationPopup
                    isOpen={isOpen}
                    migrationData={migrationData}
                />
            )}
            <AccountSelectionPopup
                isOpen={isAccountSelectionOpen}
                onClose={handleAccountSelectionClose}
            />
        </>
    );
}
